import { useCallback, useEffect, useMemo } from "react";
import { useOpportunityStagesStore } from "@/modules/opportunities/opportunityStagesStore";
import { EntitySelector } from "@/components/EntitySelector";
import { type OpportunityStage } from "@forgepoint/shared";

interface OpportunityStageSelectorProps {
  currentlySelectedId: string | null;
  onSelectId: (stageId: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Opportunity stage selector using the EntitySelector pattern.
 * Fetches active stages from the store and presents them in a searchable dropdown.
 * @param props - Selector configuration including current selection and change handler
 */
export function OpportunityStageSelector({
  currentlySelectedId,
  onSelectId,
  placeholder = "Select a stage...",
  className,
  disabled = false,
}: OpportunityStageSelectorProps) {
  const { stages, isLoading, hasFetched, fetchStages } =
    useOpportunityStagesStore();

  useEffect(() => {
    if (!hasFetched) {
      fetchStages();
    }
  }, [hasFetched, fetchStages]);

  const activeStagesMap = useMemo(() => {
    const map = new Map<string, OpportunityStage>();
    for (const [id, stage] of stages) {
      if (!stage.archivedAt) {
        map.set(id, stage);
      }
    }
    return map;
  }, [stages]);

  const fetchEntities = useCallback(
    async (search?: string) => {
      if (!hasFetched) {
        await fetchStages();
      }
      const currentStages = useOpportunityStagesStore.getState();
      const active = currentStages.getActiveStages();
      if (!search) {
        return active.map((s) => s.id);
      }
      const searchLower = search.toLowerCase();
      return active
        .filter((s) => s.name.toLowerCase().includes(searchLower))
        .map((s) => s.id);
    },
    [hasFetched, fetchStages],
  );

  const getDisplayName = useCallback((stage: OpportunityStage) => {
    return stage.name;
  }, []);

  return (
    <EntitySelector<OpportunityStage>
      entities={activeStagesMap}
      listLoading={isLoading}
      fetchEntities={fetchEntities}
      getDisplayName={getDisplayName}
      currentlySelectedId={currentlySelectedId}
      onSelectId={onSelectId}
      placeholder={placeholder}
      searchPlaceholder="Search stages..."
      className={className}
      disabled={disabled}
      entityLabel="stages"
    />
  );
}
